import { IUser } from 'src/utils/interfaces/user';
import { useLocalization } from 'src/utils/useLocalization';
import React, { useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { DealerManagementService } from '../../../../../../services/dealer-management.service';
import DialogModal from 'src/components/ui/dialogModal';
import Dropdown from 'src/components/ui/dropdownMenuTable/dropdownMenuTable';
import { HttpStatusCode } from '../../../../../../utils/enums/http-method.enum';
import OptionsIcon from 'src/components/dealer-mgmt/options-icon';

interface IPropsTable {
    items: Array<IPropsTableItem>;
    onClick: (name: string, dealerIds?: string) => void;
    updateData: () => void;
    setIsSuccessDeactivate: (open: boolean) => void;
    setIsSuccessActivate: (open: boolean) => void;
    setIsSuccessDeleted: (open: boolean) => void;
    setIsError: (open: boolean) => void;
    setIsErrorISA: (open: boolean) => void;
}

interface IPropsTableItem {
    FIRSTNAME: string;
    LASTNAME: string;
    LOGIN: string;
    PARTYID: number;
    PERMISSIONEXISTS: number;
    STATUS: number;
    DEALERIDS: string;
    STATUSTEXT: string;
    ID: string;
}

export interface ITableItemProps {
    data: IPropsTableItem;
    selectedIds: Set<string>;
    onClick: (userId: string, dealerIds?: string) => void;
    updateData: () => void;
    setIsSuccessDeactivate: (open: boolean) => void;
    setIsSuccessActivate: (open: boolean) => void;
    setIsSuccessDeleted: (open: boolean) => void;
    setIsError: (open: boolean) => void;
    setIsErrorISA: (open: boolean) => void;
    setIsLoadingISA: (open: boolean) => void;
}

const MenuItem = ({
    userId,
    status,
    deleteUserHandler,
    suspendHandler,
    reinstateHandler,
}: {
    userId: string;
    status: string;
    dealerids?: string;
    userName: string;
    deleteUserHandler: Function;
    suspendHandler: Function;
    reinstateHandler: Function;
}) => {
    return (
        <>
            <li
                className='menu-items'
                onClick={() => {
                    deleteUserHandler();
                }}
            >
                <span className='delete'>Delete</span>
            </li>
            {status === 'Suspended' && (
                <li
                    className='menu-items'
                    onClick={() => {
                        reinstateHandler(userId);
                    }}
                    data-testid={`table-item-activate-${userId}`}
                >
                    <span>Reinstate</span>
                </li>
            )}
            {status === 'Active' && (
                <li
                    className='menu-items'
                    onClick={() => {
                        suspendHandler(userId);
                    }}
                    data-testid={`table-item-deactivate-${userId}`}
                >
                    <span>Suspend</span>
                </li>
            )}
        </>
    );
};

export const TableItem = React.memo((props: ITableItemProps) => {
    const [open, setOpen] = useState(false);
    const { addNewUser } = useLocalization();
    const { data } = props;

    //
    const [rowUserId, setRowUserId] = useState<string>('');
    const [modalOpen, setModalOpen] = useState<string>('');
    const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);

    enum UserModalStatus {
        DELETE = 'DELETE',
        SUSPEND = 'SUSPEND',
        REINSTATE = 'REINSTATE',
    }

    const SUSPENDED_STATUS = 1;
    const ACTIVATED_STATUS = 0;

    // Holds the active service instance so cancel can abort the in-flight request.
    const activeServiceRef = useRef<DealerManagementService | null>(null);

    const handleDeleteUserModal = () => {
        setModalOpen(UserModalStatus.DELETE);
    };

    const deleteUserCall = async () => {
        const dealerManagementService = new DealerManagementService();
        activeServiceRef.current = dealerManagementService;
        try {
            const response = await dealerManagementService.deleteUser(
                data.ID,
                data.LOGIN,
            );
            if (response.status !== HttpStatusCode.SUCCESS) {
                throw new Error('Error deleting the user');
            }
            return response;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const handleModalCancel = () => {
        if (modalIsLoading && activeServiceRef.current) {
            activeServiceRef.current.cancelRequest();
            activeServiceRef.current = null;
        }
        setModalIsLoading(false);
        setModalOpen('');
    };

    const activateDeactivateCall = async (status: number) => {
        const dealerManagementService = new DealerManagementService();
        activeServiceRef.current = dealerManagementService;
        try {
            return await dealerManagementService.updateUserStatus(
                rowUserId,
                status,
            );
        } catch (error) {
            if ((error as any)?.code === 'ERR_CANCELED') return undefined;
            console.error(error);
        }
    };

    const handleSuspendModalOpen = (id: string) => {
        setRowUserId(id);
        setModalOpen(UserModalStatus.SUSPEND);
    };

    const handleReinstateModalOpen = (id: string) => {
        setRowUserId(id);
        setModalOpen(UserModalStatus.REINSTATE);
    };

    const suspendModalConfirm = () => {
        setModalIsLoading(true);
        activateDeactivateCall(SUSPENDED_STATUS)
            .then((res) => {
                if (res === undefined) return;
                res?.status === HttpStatusCode.SUCCESS
                    ? props.setIsSuccessDeactivate(true)
                    : props.setIsError(true);
            })
            .catch((error) => {
                if ((error as any)?.code !== 'ERR_CANCELED') {
                    props.setIsError(true);
                }
            })
            .finally(() => {
                props.updateData();
                setModalIsLoading(false);
                setModalOpen('');
                activeServiceRef.current = null;
            });
    };
    const reinstateModalConfirm = () => {
        setModalIsLoading(true);
        activateDeactivateCall(ACTIVATED_STATUS)
            .then((res) => {
                if (res === undefined) return;
                res?.status === HttpStatusCode.SUCCESS
                    ? props.setIsSuccessActivate(true)
                    : props.setIsError(true);
            })
            .catch((error) => {
                if ((error as any)?.code !== 'ERR_CANCELED') {
                    props.setIsError(true);
                }
            })
            .finally(() => {
                props.updateData();
                setModalIsLoading(false);
                setModalOpen('');
                activeServiceRef.current = null;
            });
    };

    const deleteUserConfirm = () => {
        setModalIsLoading(true);
        deleteUserCall()
            .then(() => {
                props.setIsSuccessDeleted(true);
            })
            .catch((error) => {
                if ((error as any)?.code !== 'ERR_CANCELED') {
                    props.setIsError(true);
                }
            })
            .finally(() => {
                props.updateData();
                setModalIsLoading(false);
                setModalOpen('');
                activeServiceRef.current = null;
            });
    };
    const getTitleContent = () => {
        switch (modalOpen) {
            case UserModalStatus.DELETE:
                return 'Delete User Account';
            case UserModalStatus.SUSPEND:
                return 'Suspend User Account';
            case UserModalStatus.REINSTATE:
                return 'Reinstate User Account';
            default:
                return '';
        }
    };
    const getBodyContent = () => {
        switch (modalOpen) {
            case UserModalStatus.DELETE:
                return 'Are you sure you want to delete this user account?';
            case UserModalStatus.SUSPEND:
                return 'Are you sure you want to suspend this user account?';
            case UserModalStatus.REINSTATE:
                return 'Are you sure you want to reinstate this user account?';
            default:
                return '';
        }
    };
    const getConfirmBtnLabel = () => {
        switch (modalOpen) {
            case UserModalStatus.DELETE:
            case UserModalStatus.SUSPEND:
            case UserModalStatus.REINSTATE:
                return 'Yes, confirm';
            default:
                return '';
        }
    };

    //
    return (
        <>
            {modalOpen !== '' && (
                <DialogModal
                    titleContent={getTitleContent()}
                    bodyContent={getBodyContent()}
                    open={modalOpen !== ''}
                    cancelBtnLabel={addNewUser.MODAL_LABEL_BUTTON_CANCEL}
                    confirmBtnLabel={getConfirmBtnLabel()}
                    onCancelHandler={handleModalCancel}
                    onConfirmHandler={() => {
                        modalOpen === UserModalStatus.DELETE
                            ? deleteUserConfirm()
                            : modalOpen === UserModalStatus.SUSPEND
                            ? suspendModalConfirm()
                            : modalOpen === UserModalStatus.REINSTATE
                            ? reinstateModalConfirm()
                            : null;
                    }}
                    isLoading={modalIsLoading}
                    keepOpenOnConfirm={true}
                />
            )}
            <div className='sl-table-row'>
                <div className='table-row col-1'>
                    <span className='list-label'>Name</span>
                    <span>
                        <a
                            onClick={() => {
                                props.onClick(data.LOGIN, data.DEALERIDS);
                            }}
                        >
                            {data.FIRSTNAME} {data.LASTNAME}
                        </a>
                    </span>
                </div>

                <div className='table-row col-2'>
                    <span className='list-label'>Email</span>
                    <span>{data.LOGIN}</span>
                </div>

                <div className='table-row col-3'>
                    <span className='list-label'>Admin</span>
                    {data.PERMISSIONEXISTS === Number('1') ? (
                        <span className='c-badge__wrapper'>
                            <span className='c-badge badge'>Admin</span>
                        </span>
                    ) : null}
                </div>

                <div className='table-row col-4'>
                    <span className='list-label'>Status</span>
                    <span>
                        {data.STATUSTEXT === 'Active' ? 'Active' : 'Suspended'}
                    </span>
                </div>

                <div className='table-row col-5'>
                    <span className='list-label'></span>
                    <span
                        className='options-icon'
                        data-testid={`table-item-${data.ID}`}
                        onClick={() => {
                            setOpen(true);
                        }}
                    >
                        <OptionsIcon />
                    </span>
                    <Dropdown
                        dropdown={open}
                        showSelected={false}
                        showSelectedIcon={false}
                        handlerChange={(value: boolean) => {
                            setOpen(value);
                        }}
                    >
                        <MenuItem
                            userId={data.ID}
                            status={data.STATUSTEXT}
                            dealerids={data.DEALERIDS}
                            userName={data.LOGIN}
                            deleteUserHandler={handleDeleteUserModal}
                            suspendHandler={handleSuspendModalOpen}
                            reinstateHandler={handleReinstateModalOpen}
                        />
                    </Dropdown>
                </div>
            </div>
        </>
    );
});

TableItem.displayName = 'TableItem';

export const Table = (props: IPropsTable) => {
    const { items } = props;
    const { dealerMgmt } = useLocalization();
    const dsmSearch = useSelector((s: IUser) => s.dsmSearch, shallowEqual);

    const selectedIds = useMemo(() => {
        const ids = new Set<string>();
        for (const u of dsmSearch.selectedUsers ?? []) ids.add(u.ID);
        return ids;
    }, [dsmSearch.selectedUsers]);

    const [isLoadingISA, setIsLoadingISA] = useState<Boolean>(false);

    return (
        <div
            className={`sl-table ${
                isLoadingISA ? 'c-spinner  c-spinner--section' : ''
            }`}
            aria-live='polite'
            aria-busy='true'
        >
            <div className='sl-table-row header-row hidden-in-list'>
                <div className='table-header col-1'>
                    <span className='u-text-truncate'>
                        {dealerMgmt.DEALER_MGMT_TABLE_HEADER_NAME}
                    </span>
                </div>
                <div className='table-header col-2'>
                    <span className='u-text-truncate'>
                        {dealerMgmt.DEALER_MGMT_TABLE_HEADER_EMAIL}
                    </span>
                </div>
                <div className='table-header col-3'>
                    <span className='u-text-truncate'>
                        {dealerMgmt.DEALER_MGMT_TABLE_HEADER_ADMIN}
                    </span>
                </div>
                <div className='table-header col-4'>
                    <span className='u-text-truncate'>
                        {dealerMgmt.DEALER_MGMT_TABLE_HEADER_STATUS}
                    </span>
                </div>
                <div className='table-header col-5 menu-header'></div>
            </div>
            {items.map((item) => {
                return (
                    <TableItem
                        key={item.ID}
                        data={item}
                        selectedIds={selectedIds}
                        onClick={props.onClick}
                        updateData={props.updateData}
                        setIsSuccessDeleted={props.setIsSuccessDeleted}
                        setIsSuccessDeactivate={props.setIsSuccessDeactivate}
                        setIsSuccessActivate={props.setIsSuccessActivate}
                        setIsError={props.setIsError}
                        setIsErrorISA={props.setIsErrorISA}
                        setIsLoadingISA={setIsLoadingISA}
                    />
                );
            })}
            {isLoadingISA && (
                <div className='c-spinner__loader'>
                    <span className='c-spinner__element'></span>
                    <span className='c-spinner__element'></span>
                    <span className='c-spinner__element'></span>
                    <span className='c-spinner__element'></span>
                    <span className='c-spinner__element'></span>
                    <span className='c-spinner__element'></span>
                    <span className='c-spinner__element'></span>
                    <span className='c-spinner__element'></span>
                </div>
            )}
        </div>
    );
};
